import { BottomSheetModal } from '@gorhom/bottom-sheet';
import React, { RefObject } from 'react';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button, { ButtonType } from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItemGroup from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface OfferActivatedBottomSheetProps {
  bottomSheetRef: RefObject<BottomSheetModal | null>;
  offerDetails: {
    successTitle: string;
    instructions: Array<{
      id: string;
      icon: string;
      text: string;
    }>;
    primaryButton: {
      buttonStyle: ButtonType;
      buttonText: string;
    };
  };
  onSubmit: () => void;
  onDismiss?: () => void;
  isLoading?: boolean;
}

// There is a existing issue in BottomSheetModal in ios and android when using prop allowScrolling.
// Modal won't preset when using allowScrolling.
// For a quick fix, use isLoading to show a loading for few moments and then show the content.
// Same method used in <AccountListBottomSheet /> component.
// When the issue is fixed in VXL, you can remove that quick fix code.
const OfferActivatedBottomSheet = ({
  bottomSheetRef,
  offerDetails,
  onSubmit,
  onDismiss,
  isLoading = false,
}: OfferActivatedBottomSheetProps) => {
  return (
    <BottomSheet
      ref={bottomSheetRef}
      onDismiss={onDismiss}
      contentContainerStyle={styles.bottomSheetContainer}
    >
      <View style={styles.container}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <View style={styles.contentContainer}>
              <Icon
                iconKey="LIST_CHECK_MARK"
                iconColor={theme.color.expressive.mint}
                iconSize={48}
                iconAccessibilityLabel="offer-activated-icon"
              />
              {offerDetails.successTitle ? (
                <View style={styles.titleContainer}>
                  <TitleLgText style={styles.successTitle}>
                    {offerDetails.successTitle}
                  </TitleLgText>
                </View>
              ) : null}

              <View style={styles.listGroupContainer}>
                <ListItemGroup
                  items={
                    offerDetails.instructions?.map((item) => ({
                      keyId: item.id,
                      leadingAccessory: {
                        id: `${item.id}-leading-accessory`,
                        name: VXLComponentId.ICON,
                        props: {
                          iconKey: item.icon,
                        },
                      },
                      primaryCopy: item.text,
                      showsChevron: false,
                      primaryNumberOfLines: 0,
                    })) ?? []
                  }
                />
              </View>
            </View>
            {offerDetails.primaryButton ? (
              <View style={styles.buttonContainer}>
                <Button
                  onPress={onSubmit}
                  label={offerDetails.primaryButton.buttonText}
                  type={offerDetails.primaryButton.buttonStyle}
                />
              </View>
            ) : null}
          </>
        )}
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  bottomSheetContainer: {
    paddingTop: 0,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    alignItems: 'center',
    flex: 1,
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  titleContainer: {
    width: '100%',
    paddingHorizontal: theme.spacing.m,
  },
  successTitle: {
    textAlign: 'center',
  },
  listGroupContainer: {
    width: '100%',
  },
  buttonContainer: {
    width: '100%',
    padding: theme.spacing.m,
  },
});

export default OfferActivatedBottomSheet;
